import { Ban, FlaskConical, Link2, ShoppingBag, TimerReset } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { toast } from 'sonner'
import { EmptyState } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { confirmModal } from '@/components/ui/modal'
import { cn } from '@/lib/utils'
import { fmtDate, fmtDateTime } from '../../shared/format'
import { useCancelTrial, useOrders } from '../api'
import type { Trial } from '../types'
import { toastError } from '../ui'
import { TrialStatusBadge } from './CrmBits'
import { CustomerLink } from './RecordLists'

const span = (t: Trial) => Math.max(1, Math.round((Date.parse(`${t.endsOn}T00:00:00Z`) - Date.parse(`${t.startedOn}T00:00:00Z`)) / 86_400_000))

function Remaining({ trial: t }: { trial: Trial }) {
  const { t: tr } = useTranslation()
  if (t.status === 'converted' || t.status === 'cancelled') return null
  const total = span(t)
  const used = Math.min(100, Math.max(0, Math.round(((total - Math.max(0, t.daysLeft)) / total) * 100)))
  const tone = t.daysLeft < 0 ? 'bg-destructive' : t.daysLeft <= 3 ? 'bg-warning' : 'bg-primary'
  return (
    <div className="w-full space-y-1 sm:w-40">
      <p className={cn('text-xs font-medium tabular-nums', t.daysLeft < 0 ? 'text-destructive' : t.daysLeft <= 3 ? 'text-warning-strong' : 'text-muted-foreground')}>
        {t.daysLeft < 0 ? tr('crm.trial.endedAgo', { count: -t.daysLeft }) : t.daysLeft === 0 ? tr('crm.trial.endsToday') : tr('crm.trial.daysLeft', { count: t.daysLeft })}
      </p>
      <div className="h-1.5 overflow-hidden rounded-full bg-muted" role="meter" aria-valuenow={used} aria-valuemin={0} aria-valuemax={100} aria-label={tr('crm.trial.usage')}>
        <div className={cn('h-full rounded-full', tone)} style={{ width: `${used}%` }} />
      </div>
    </div>
  )
}

type Props = { rows: Trial[]; showCustomer?: boolean; canWrite: boolean; onExtend: (t: Trial) => void; onConvert: (t: Trial) => void }

/** Deneme lisansları: modüller, kalan süre, uzatma geçmişi ve siparişe bağlama. */
export function TrialList({ rows, showCustomer, canWrite, onExtend, onConvert }: Props) {
  const { t } = useTranslation()
  const cancel = useCancelTrial()
  const orders = useOrders().data ?? []
  if (!rows.length) return <EmptyState icon={FlaskConical} title={t('crm.trial.empty')} />

  const onCancel = async (trial: Trial) => {
    if (!(await confirmModal({ title: t('crm.trial.cancelTitle'), description: t('crm.trial.cancelBody'), confirmLabel: t('crm.trial.cancel'), destructive: true }))) return
    cancel.mutate(trial.id, { onSuccess: () => toast.success(t('crm.trial.cancelled')), onError: toastError })
  }

  return (
    <ul className="divide-y">
      {rows.map((tr) => {
        const open = tr.status === 'active' || tr.status === 'expired'
        const order = tr.orderId ? orders.find((o) => o.id === tr.orderId) : undefined
        return (
          <li key={tr.id} className="flex flex-wrap items-center gap-x-4 gap-y-3 px-4 py-3">
            <div className="flex min-w-0 flex-1 basis-64 items-start gap-3">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-violet/12 text-violet">
                <FlaskConical className="size-4" aria-hidden />
              </span>
              <div className="min-w-0 space-y-1">
                <div className="flex flex-wrap items-center gap-2">
                  {showCustomer ? <CustomerLink id={tr.customerId} tab="delivery" /> : null}
                  <TrialStatusBadge status={tr.status} />
                </div>
                <div className="flex flex-wrap gap-1">
                  {tr.modules.map((m) => (
                    <span key={m} className="rounded-md bg-muted px-1.5 py-0.5 text-xs">
                      {m}
                    </span>
                  ))}
                </div>
                <p className="text-xs text-muted-foreground">
                  {fmtDate(tr.startedOn)} – {fmtDate(tr.endsOn)} · {tr.createdBy}
                  {tr.extensions.length ? ` · ${t('crm.trial.extensionCount', { count: tr.extensions.length })}` : ''}
                </p>
                {tr.extensions.length ? (
                  <p className="text-xs text-muted-foreground" title={tr.extensions.map((e) => `${fmtDateTime(e.at)} · +${e.days} · ${e.reason}`).join('\n')}>
                    {t('crm.trial.lastExtension', { days: tr.extensions.at(-1)!.days, reason: tr.extensions.at(-1)!.reason })}
                  </p>
                ) : null}
                {order ? (
                  <Link to={`/customers/${tr.customerId}/orders`} className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
                    <ShoppingBag className="size-3.5" aria-hidden />
                    {t('crm.trial.linkedOrder', { number: order.number })}
                  </Link>
                ) : null}
              </div>
            </div>
            <Remaining trial={tr} />
            {canWrite && open ? (
              <div className="flex flex-wrap gap-2">
                <Button size="sm" variant="ghost" className="hover:text-destructive" onClick={() => void onCancel(tr)} disabled={cancel.isPending}>
                  <Ban />
                  {t('crm.trial.cancel')}
                </Button>
                <Button size="sm" variant="outline" onClick={() => onExtend(tr)}>
                  <TimerReset />
                  {t('crm.trial.extend')}
                </Button>
                <Button size="sm" onClick={() => onConvert(tr)}>
                  <Link2 />
                  {t('crm.trial.convert')}
                </Button>
              </div>
            ) : null}
          </li>
        )
      })}
    </ul>
  )
}
